import type { PosDraftPayment, PosFiscalMode } from '../../domain/posTypes';
import type { CartEvaluationResult } from '../../../promotions/public-api';
import { formatPromoMoney, summarizeCheckoutDiscounts } from '../../../promotions/public-api';

/** Filas visibles sin scroll; arriba de esto la lista scrollea dentro del panel. */
export const CHECKOUT_HERO_PROMO_SCROLL_MAX_ROWS = 3;

const MP_QR_SUMMARY_HINT =
  'El cliente paga con el QR de Mercado Pago. Puede tardar unos segundos.';

export { MP_QR_SUMMARY_HINT as CHECKOUT_MP_QR_SUMMARY_HINT };

/** Dos columnas solo en tablet/desktop real, no en celular landscape. */
export function resolveCheckoutLayoutWide(windowWidth: number, windowHeight: number): boolean {
  const shortestSide = Math.min(windowWidth, windowHeight);
  return shortestSide >= 600 && windowWidth >= 720;
}

export function resolveCheckoutShellMaxHeight(windowHeight: number): number {
  return Math.round(windowHeight * 0.9);
}

export type CheckoutHeroPromoItem = {
  id: string;
  name: string;
  amount: number;
};

export type CheckoutHeroPromoDisplay =
  | { kind: 'none' }
  | {
      kind: 'items';
      items: CheckoutHeroPromoItem[];
    };

export function resolveCheckoutHeroPromoDisplay(
  cartEvaluation: CartEvaluationResult | null | undefined
): CheckoutHeroPromoDisplay {
  if (!cartEvaluation || cartEvaluation.discountTotal <= 0) {
    return { kind: 'none' };
  }

  const rows = summarizeCheckoutDiscounts(cartEvaluation);
  if (rows.length === 0) {
    return { kind: 'none' };
  }

  const aggregated = new Map<string, CheckoutHeroPromoItem>();
  for (const row of rows) {
    const existing = aggregated.get(row.promotionId);
    if (existing) {
      existing.amount += row.discountAmount;
    } else {
      aggregated.set(row.promotionId, {
        id: row.promotionId,
        name: row.promotionName,
        amount: row.discountAmount,
      });
    }
  }

  return { kind: 'items', items: [...aggregated.values()] };
}

export function formatCheckoutHeroPromoAmount(amount: number): string {
  return `−${formatPromoMoney(amount)}`;
}

export function getCheckoutHeroMetaLines(
  saleFiscalMode: PosFiscalMode,
  options?: { showFiscalHint?: boolean }
): Array<{ label: string; value: string }> {
  const lines: Array<{ label: string; value: string }> = [];
  const showFiscalHint = options?.showFiscalHint !== false;

  if (showFiscalHint) {
    lines.push({
      label: 'Comprobante',
      value:
        saleFiscalMode === 'fiscal'
          ? 'Factura ARCA'
          : 'Ticket interno (sin ARCA)',
    });
  }

  return lines;
}

export function getCheckoutConfirmLabel(
  hasBlockingStockIssue: boolean,
  paymentDrafts: PosDraftPayment[]
): string {
  if (hasBlockingStockIssue) return 'Ir a Stock';
  const mpQrOnly = isMpQrOnlyDrafts(paymentDrafts);
  const hasMpQr = paymentDrafts.some((d) => d.methodId === 'mercadopago_qr');
  if (mpQrOnly) return 'Generar QR y cobrar';
  if (hasMpQr) return 'Confirmar y generar QR';
  return 'Confirmar cobro';
}

export function isMpQrOnlyDrafts(paymentDrafts: PosDraftPayment[]): boolean {
  return (
    paymentDrafts.some((d) => d.methodId === 'mercadopago_qr') &&
    paymentDrafts.every((d) => d.methodId === 'mercadopago_qr')
  );
}
